/* eslint-disable react/jsx-no-target-blank */
import BgSection from 'components/BgSection/index';
import PlusButton from 'components/PlusButton';
import Shares from 'components/Shares';
import GoneTableFrame from 'containers/GoneTableFrame/Container';
import NewWordsFrame from 'containers/NewWordsFrame/Container';
import OldNewWordsFrame from 'containers/OldNewWordsFrame/Container';
import OmonymsFrame from 'containers/OmonymsFrame/Container';
import 'containers/ResearchApp/index.scss';
import IframeResizer from 'iframe-resizer-react';
import React, {FC, useRef, useState} from 'react';
import {flatBem} from 'utils/bem';
import {getDataUrl} from 'utils/dataUrls';
import {Configurator} from 'utils/makeConfigurator';

const b = flatBem('ResearchApp');

export type TableRow = {
  old: string;
  new: string;
  example: string;
  exampleYear: string;
};

type Props = {
  conf: Configurator;
};

const Component: FC<Props> = ({conf}) => {
  const [newWordsVisible] = useState(true);
  const pRef1 = useRef<HTMLDivElement>(null);

  const isMobile = conf('isMobile', false);
  if (isMobile) {
    document.getElementById('research-app')!.classList.add('ra-mobile');
  }

  return (
    <div className={b()}>
      <div className={b('top-title')}>Как меняется лексика русского языка</div>
      <div className={b('top-date')}>
        По данным Яндекса и центра <a href="https://prozhito.org/">Прожито</a> ЕУСПб
      </div>
      <div className={b('paragraph')}>
        22 ноября родился Владимир Даль, составитель «Толкового словаря живого великорусского
        языка», — в день его рождения в России празднуют День словаря. Чтобы отметить эту дату, мы
        собрали примеры изменений, произошедших в лексике русского языка в последние сто лет.
      </div>
      <div className={b('frame')}>
        <OldNewWordsFrame />
      </div>
      <div className={b('paragraph')} ref={pRef1} style={{position: 'relative'}}>
        Мы сравнили записи из личных дневников, сделанные с 1900 по 1940 год{' '}
        <PlusButton
          tooltipText={
            <>
              Дневниковые записи нам предоставил центр <a href="https://prozhito.org/">Прожито</a>{' '}
              ЕУСПб, который собирает, изучает и публикует личные тексты.
            </>
          }
          parentDivRef={pRef1}
        />
        , и тексты статей и комментариев в современных блогах{' '}
        <PlusButton
          tooltipText={
            'В качестве корпуса современных текстов мы использовали статьи и комментарии на Яндекс.Дзене — контентной платформе, на которой регулярно публикуются более 45 тысяч блогеров и медиа.\n'
          }
          parentDivRef={pRef1}
        />
        . Вместе с лингвистами нашли слова, частотности или контексты употребления которых в двух
        корпусах сильно отличались.
      </div>
      <div
        className={b(['paragraph', 'fixed-width'])}
        style={isMobile ? {marginBottom: -5, marginTop: 15} : {marginBottom: -10, marginTop: 30}}
      >
        <BgSection
          allCutBlock={{
            title: 'Как и что мы считали',
            initialState: false,
          }}
          mainBlock={{
            content: (
              <>
                Чтобы найти примеры слов, которые уходят из языка или, наоборот, появились в нём
                сравнительно недавно, мы сравнивали частотности (доли от всех словоупотреблений)
                каждого слова в двух корпусах. Первый корпус составляют дневниковые записи из
                собрания центра Прожито ЕУСПб, сделанные в период с 1900 по 1940 год. Второй —
                тексты, которые публиковались на платформе Яндекс.Дзен (как статьи, так и
                комментарии) с 2019 по 2021 год.
                <br />
                <br />
                Оба корпуса подходят для изучения живой речи своего периода, так как их тексты
                написаны в основном не профессиональными авторами (писателями, журналистами), а
                обычными носителями языка и не проходили редактуру и корректуру. Профессиональные
                авторы часто пишут более архаично, у них больше словарный запас, они используют
                разные стили — всё это отличает их тексты от того, как реально говорят вокруг.
                Редакторы и корректоры озабочены точным следованием норме, записанной в словарях и
                грамматиках, поэтому тексты, прошедшие редактуру и корректуру, неизбежно отстают от
                живого современного языка.
                <br />
                <br />
                При этом между текстами дневников и статей в Дзене есть очевидные жанровые и
                тематические отличия. Например, слово <i>мамуся</i>, отмеченное личным отношением
                автора и совершенно нормальное для жанра дневника (причём, возможно, и
                современного), сложно представить себе в статье в Дзене; слово <i>озорство</i>{' '}
                тематически ближе к описанию личных событий или знакомых, поэтому неудивительно, что
                в статьях Дзена оно встречается гораздо реже. Мы старались учитывать эти отличия и
                не брали такие слова в примеры изменений. Кроме того, не брали слова, которые
                связаны с ушедшими <i>(кучер, двуколка)</i> или новыми <i>(интернет, смартфон)</i>{' '}
                явлениями.
                <br />
                <br />
                Для поиска слов, у которых изменилось основное значение, мы проверяли, насколько
                изменился контекст употребления данного слова. С помощью алгоритма{' '}
                <a href="https://habr.com/ru/post/446530/">word2vec</a> получили векторы связей
                слова с другими словами в каждом из двух корпусов, а потом нашли слова, векторы
                которых в двух корпусах сильно отличаются.
              </>
            ),
          }}
        />
      </div>
      <div className={b('paragraph')}>
        Вот сто слов, которые сегодня употребляются в разы реже, чем в дневниковых записях,
        сделанных в период до Великой Отечественной войны.
      </div>
      <div className={b(['frame', 'fixed-width'])} id="old">
        <GoneTableFrame />
      </div>
      <div className={b('paragraph')} style={{display: 'none'}}>
        Борис Иомдин, заведующий сектором Института русского языка им. В. В. Виноградова РАН,
        научный сотрудник Яндекса
        <br />
        <br />
        Грамматика — область регулярного, а лексика — область индивидуального. Поэтому постепенное
        исчезновение одних слов и замена их другими — во многом непредсказуемый процесс. Однако
        можно выделить несколько тенденций.
        <br />
        <br />
        Прежде всего бросаются в глаза изменения написания...
        <br />
        <br />
        [Читать дальше]
      </div>
      <div className={b(['frame', 'fixed-width'])}>
        <BgSection
          avatarBlock={{
            imgUrl: `https://yastatic.net/s3/milab/2021/local-words/i/boris-02.jpg`,
            title: <>БОРИС ИОМДИН</>,
            subtitle: (
              <>
                ЗАВЕДУЮЩИЙ СЕКТОРОМ ИНСТИТУТА РУССКОГО ЯЗЫКА ИМ. В. В. ВИНОГРАДОВА РАН, учёный
                Яндекса
              </>
            ),
          }}
          mainBlock={{
            title: <>О некоторых тенденциях</>,
            content: (
              <>
                Грамматика — область регулярного, а лексика — область индивидуального. Поэтому
                постепенное исчезновение одних слов и замена их другими — во многом непредсказуемый
                процесс. Однако можно выделить несколько тенденций.
              </>
            ),
          }}
          bottomCutBlock={{
            title: <>Читать далее</>,
            content: (
              <>
                Прежде всего бросаются в глаза изменения написания:{' '}
                <i>
                  мятель &gt; метель, фойэ &gt; фойе, купэ &gt; купе, танцовать &gt; танцевать,
                  имянинник &gt; именинник, попрежнему &gt; по-прежнему
                </i>
                . К ним примыкают небольшие звуковые изменения, тоже отражающиеся на письме:{' '}
                <i>
                  галстух &gt; галстук, конфекты &gt; конфеты, шкап &gt; шкаф, биллиард &gt;
                  бильярд, баталлион &gt; батальон, противуположный &gt; противоположный
                </i>
                . Устраняются нерегулярности, например, в чередованиях: <i>дочесть &gt; дочитать</i>
                , по аналогии с <i>читать</i>.
                <br />
                <br />
                Часто заменяются глагольные приставки:{' '}
                <i>
                  проконспектировать &gt; законспектировать, выкупаться &gt; искупаться,
                  срепетировать &gt; отрепетировать, вздорожать &gt; подорожать, расцеловаться &gt;
                  поцеловаться
                </i>
                &nbsp;(приставка <i>по-</i>&nbsp;вообще используется теперь существенно чаще).
                Уходят многие слова с суффиксами, выражающими абстрактное значение свойства или
                процесса: <i>дрянность, талантливость, весёлость, отобрание, напечатание</i>
                .&nbsp;Как ни странно, уходят некоторые феминитивы:{' '}
                <i>делегатка, консерваторка, музыкантша, домовладелица, жилица</i>.
                <br />
                <br />
                Интересно отметить нередкие случаи, когда заимствования заменяются словами с
                исконными корнями:{' '}
                <i>
                  телефонировать &gt; звонить, визитировать &gt; ходить в гости, конфузиться &gt;
                  смущаться, отрекомендоваться &gt; представиться, иллюминованный &gt; освещённый,
                  аэроплан &gt; самолёт, забаллотировать &gt; не выбрать
                </i>
                .
                <br />
                <br />
                Часто меняются слова, выражающие отрицательную или положительную оценку — им вообще
                свойственно быстрое выцветание, замена новыми более яркими. Уходят из активного
                лексикона слова <i>томительно, вздор, заправи́ла, лганьё, прескверно ... </i>
                Выражение высокой степени приставкой <i>пре-</i>&nbsp;вообще становится архаичным:
                уже не говорят{' '}
                <i>
                  премило, препотешно, прекурьёзно, превесело, прегнусно, преглупо, прескучно,
                  прегадко
                </i>
                .
              </>
            ),
          }}
        />
      </div>
      <div className={b(['frame', 'fixed-width'])}>
        <BgSection
          avatarBlock={{
            imgUrl: getDataUrl(`./assets/tatyana.jpg`),
            title: <>Татьяна Резникова</>,
            subtitle: (
              <>
                научный сотрудник РАНХиГС и Центра междисциплинарных исследований человеческого
                потенциала НЦМУ
              </>
            ),
          }}
          mainBlock={{
            title: <>О сужении контекстов употребления</>,
            content: (
              <>
                Исчезновение слов из языка обычно сопровождается сокращением числа контекстов, в
                которых они могут выступать. Например, прилагательное <i>тягостный</i>&nbsp;в
                современном языке связано в основном с эмоциональным состоянием: <i>тягостными</i>
                &nbsp;могут быть чувства, переживания, раздумья. Раньше же <i>тягостным</i>
                &nbsp;называли еще и положение, время, период жизни – сегодня бы мы использовали в
                этих контекстах слово <i>трудный</i>. <i>Тягостными</i>&nbsp;иногда оказывались даже
                люди – так говорили про спутников, гостей – сейчас мы бы могли сказать, что эти люди
                нам <i>в тягость</i>.
              </>
            ),
          }}
          bottomCutBlock={{
            title: <>Читать далее</>,
            content: (
              <>
                Слово <i>измятый</i>&nbsp;в сегодняшней речи значительно уступает по частотности
                своим «конкурентам» с тем же корнем – лексемам <i>мятый</i>&nbsp;и <i>помятый</i>.
                Но если в отношении предметов одежды (платье, пиджак и др.), а также постели или
                клочков бумаги <i>измятый</i>&nbsp;еще может употребляться, то лицо усталого,
                невыспавшегося человека сегодня назовут только <i>помятым</i>, хотя раньше контексты
                типа <i>человек с измятым лицом</i>&nbsp;встречались довольно часто.
                <br />
                <br />
                <i>
                  Курьезно, что все только об этом и говорят; курьезно вышло, когда…, курьезно
                  получается
                </i>
                &nbsp;– во всех этих случаях нам бы сейчас привычнее было видеть слово{' '}
                <i>забавно</i>. В современном употреблении чаще всего <i>курьезно</i>&nbsp;что-то
                выглядит или звучит, остальные контексты постепенно уходят из языка.
                <br />
                <br />
                Технический глагол <i>зондировать</i>&nbsp;в бытовом значении сейчас выступает в
                составе устойчивого выражения <i>зондировать почву</i>. Так говорят, когда кто-то
                пытается осторожно что-то узнать, выяснить. Оказывается, что раньше это значение
                распространялось на более широкий круг контекстов. Можно было <i>зондировать</i>
                &nbsp;не только почву, но и кого-то, а также про что-то (
                <i>зондировать про Америку</i>) или относительно чего-то (
                <i>зондировать относительно самочувствия</i>).
              </>
            ),
          }}
        />
      </div>
      <div className={b('paragraph')}>
        Слова не только уходят, язык постоянно пополняется новыми словами. И дело не только в том,
        что появляются вещи, которые надо как-то называть <i>(парковка, кроссовки, чипсы)</i>. Есть
        и такие слова, к которым мы давно привыкли, но которых всего сто лет назад ещё не было в
        русском языке — во всяком случае, они не встречаются в дневниках первой половины XX века.
      </div>
      <div
        className={b('frame')}
        ref={_elem => {
          // if (!elem || newWordsVisible) return;
          // const observer = new IntersectionObserver(
          //   a => {
          //     console.log('callback', a[0].intersectionRatio);
          //     setNewWordsVisible(true);
          //   },
          //   {
          //     threshold: 1.0,
          //   },
          // );
          // observer.observe(elem);
        }}
        id="new"
      >
        {newWordsVisible ? <NewWordsFrame /> : null}
      </div>
      <div className={b('paragraph')}>
        Бывают и другие изменения: слово не уходит, но сильно меняет своё основное значение. Чтобы
        найти такие примеры, мы сравнивали лексические окружения каждого слова в двух корпусах — то
        есть проверяли, насколько изменились контексты их употребления.
      </div>
      <div className={b('frame')} id="homonyms">
        <OmonymsFrame />
      </div>
      <div className={b(['frame', 'fixed-width'])}>
        <BgSection
          mainBlock={{
            title: <>«Переводчик» на язык первой половины XX века</>,
            content: (
              <>
                Наши коллеги, создатели{' '}
                <a href="https://yandex.ru/lab/yalm" target="_blank">
                  Балабобы
                </a>
                , узнав об исследовании, придумали сделать нейросетевой «переводчик», который
                стилизует текст под старую дневниковую запись. Работает он, можно сказать,
                полузамечательно и часто выдаёт что-то странное, но иногда его перевод очень хорош.
              </>
            ),
          }}
          bottomCutBlock={{
            title: <>Поиграть</>,
            content: <>...</>,
          }}
        />
      </div>
      {/* eslint-disable-next-line no-constant-condition */}
      {0 ? null : (
        <>
          <div className={b('iframe-wrapper')} style={{background: '#FE3B13'}}>
            <div
              style={
                isMobile
                  ? {width: '100%'}
                  : {width: 750, height: 500, margin: '0 auto', paddingTop: 39, paddingBottom: 39}
              }
            >
              <IframeResizer
                src={
                  // eslint-disable-next-line no-constant-condition
                  // 1
                  // ? `/static/quiz-iframe/index.html`
                  `https://milab.s3.yandex.net/2021/old-words/front-v1/quiz-iframe/index.html`
                }
                style={{width: '1px', minWidth: '100%', height: 'inherit'}}
                frameBorder="0"
                id="quiz"
                autoResize={false} // fix 320px first load problem
                heightCalculationMethod="max"
                // checkOrigin={['https://milab.s3.yandex.net', 'https://yastatic.net']}
              />
            </div>
          </div>
        </>
      )}
      <div className={b('shares')} style={{marginTop: 100, marginBottom: 100}}>
        <Shares customUrl={`https://yandex.ru/company/researches/2021/old-words`} />
      </div>
    </div>
  );
};

export default Component;
